import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Button, Typography, Empty } from 'antd';
import { DeleteOutlined, CheckOutlined, InfoCircleOutlined, WarningOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { notificationsApi } from '../services/api-services';

const { Text } = Typography;

interface InternalNotificationListProps {
  unreadCount: number;
  onUnreadCountChange: (count: number) => void;
}

export const InternalNotificationList = forwardRef<any, InternalNotificationListProps>(({ unreadCount, onUnreadCountChange }, ref) => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res: any = await notificationsApi.list({ limit: 99, unread_only: true });
      setNotifications(res.items || []);
      onUnreadCountChange(res.total || 0);  // 使用 total 作为未读数
    } catch (e: any) {
      console.error('Failed to load notifications:', e);
      setError(e.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  useEffect(() => {
    loadNotifications();
    
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const handleMarkAsRead = async (notifId: number) => {
    try {
      await notificationsApi.markAsRead(notifId);
      await loadNotifications();
    } catch (e) {
      console.error('Failed to mark as read:', e);
    }
  };

  const handleDelete = async (notifId: number) => {
    try {
      await notificationsApi.delete(notifId);
      await loadNotifications();
    } catch (e) {
      console.error('Failed to delete notification:', e);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      await loadNotifications();
    } catch (e) {
      console.error('Failed to mark all as read:', e);
    }
  };

  useImperativeHandle(ref, () => ({
    handleMarkAllAsRead,
  }));

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px 0' }}>加载中...</div>;
  }

  if (error) {
    return <div style={{ textAlign: 'center', padding: '40px 0', color: '#ff4d4f' }}>{error}</div>;
  }

  if (notifications.length === 0) {
    return <Empty description="暂无通知" style={{ marginTop: 40 }} />;
  }

  return (
    <>
      <div>
        {notifications.map((notif) => (
          <div
            key={notif.id}
            style={{
              padding: '12px',
              background: notif.is_read ? 'var(--plat-bg-card)' : '#e6f7ff',
              border: '1px solid var(--plat-border)',
              borderRadius: 'var(--plat-radius-sm)',
              marginBottom: 8,
              transition: 'all 0.2s',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = 'var(--plat-shadow)';
              const deleteBtn = e.currentTarget.querySelector('.delete-btn') as HTMLElement;
              if (deleteBtn) deleteBtn.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              const deleteBtn = e.currentTarget.querySelector('.delete-btn') as HTMLElement;
              if (deleteBtn) deleteBtn.style.opacity = '0';
            }}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(notif.id)}
              className="delete-btn"
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                opacity: 0,
                transition: 'opacity 0.2s',
                padding: '0 4px',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
              {getIcon(notif.notification_type)}
              <div style={{ flex: 1, marginLeft: 12 }}>
                <div>
                  <Text strong>{notif.title}</Text>
                  {!notif.is_read && (
                    <span style={{ 
                      display: 'inline-block', 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      background: '#ff4d4f', 
                      marginLeft: 8 
                    }} />
                  )}
                </div>
                <div style={{ marginTop: 8, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                  {notif.content}
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(notif.created_at).toLocaleString('zh-CN')}
                  </Text>
                </div>
              </div>
            </div>
            {!notif.is_read && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => handleMarkAsRead(notif.id)}
                  style={{ fontSize: 12, padding: '0 4px' }}
                >
                  标为已读
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
});
